import { cn } from '@virginexperiencedays/tailwind';
import { IconStar } from '../../icons/IconStar';
import { IconSpeechBubble } from '../../icons/IconSpeechBubble';

import type { HTMLAttributes } from 'react';

type ReviewProps = {
  rating: number;
  title: string;
  content: string;
  reviewerName: string;
  dateCreated: string;
};

type ReviewResponseProps = {
  responderName?: string;
  content: string;
};

export type Props = ReviewProps & {
  response: ReviewResponseProps | null;
} & HTMLAttributes<HTMLDivElement>;

// This follows Composition Pattern + Atomic Design
// Usage Reference: See implementation in storybook template mocks

export const PDPReviewStarRating = ({
  rating,
  className,
}: Readonly<Pick<Props, 'rating' | 'className'>>) => (
  <div className={cn('flex flex-nowrap gap-0.5', className)} data-rating={rating}>
    {Array.from({ length: 5 }, (_, i) => (
      <IconStar
        key={i}
        width={18}
        height={18}
        type={i < rating ? 'full' : 'empty'}
        shadedcolor="var(--text-neutral-strong)"
        emptycolor="transparent"
        bordercolor="var(--text-neutral-strong)"
      />
    ))}
  </div>
);

export const PDPReviewMetadata = ({
  reviewerName,
  dateCreated,
  className,
  ...props
}: Pick<ReviewProps, 'reviewerName' | 'dateCreated'> & HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn('text-neutral-faded flex flex-nowrap gap-2 pt-3 text-xs leading-5', className)}
      {...props}
    >
      <span>Reviewed By:</span>
      <span>{reviewerName}</span>
      {dateCreated && <span>{dateCreated}</span>}
    </div>
  );
};

export const PDPReviewResponse = ({
  content,
  responderName,
  className,
}: ReviewResponseProps & HTMLAttributes<HTMLDivElement>) => {
  if (!content) return null;
  return (
    <div className={cn('border-background-primary-faded border-l-4', className)}>
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <IconSpeechBubble className="min-w-5" />
          <p className="text-brand font-semibold leading-5">Response from Virgin Experience Days</p>
        </div>
        <p className="text-base">{content}</p>
        {responderName && (
          <p className="text-neutral-faded flex flex-nowrap gap-2 pt-3 text-xs">
            Response from: <span className="pl-1">{responderName}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export const PDPReviewCardTitle = ({ title }: Pick<Props, 'title'>) => {
  return (
    <p className="text-neutral-strong font-semibold leading-6 md:text-lg md:leading-7">
      &ldquo;{title}&rdquo;
    </p>
  );
};

export const PDPReviewCardContent = ({ content }: Pick<Props, 'content'>) => {
  return <p className="text-neutral text-base">{content}</p>;
};

export const PDPReviewCard = ({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1.5', className)} {...props}>
    {children}
  </div>
);
